import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LOGO_MARK_URL } from "@/data/content";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    const inputCls = "w-full rounded-xl border border-white/10 bg-[#0F1420] px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500";

    return (
        <main className="flex min-h-screen items-center justify-center bg-hive-bg px-4" data-testid="admin-login-page">
            <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-hive-card p-8">
                <div className="flex flex-col items-center text-center">
                    <img src={LOGO_MARK_URL} alt="Innovative Fusion Hives" className="h-20 w-20 rounded-2xl object-cover ring-1 ring-white/20 shadow-[0_4px_24px_rgba(245,158,11,0.15)]" />
                    <h1 className="mt-4 font-display text-xl font-bold text-white">Admin Dashboard</h1>
                    <p className="mt-1 text-xs text-slate-500">Authorized access only</p>
                </div>
                <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
                    <div>
                        <label htmlFor="al-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Email</label>
                        <input id="al-email" type="email" required data-testid="admin-login-email-input" className={inputCls}
                            value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@innovativefusionhives.com" autoComplete="username" />
                    </div>
                    <div>
                        <label htmlFor="al-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Password</label>
                        <input id="al-password" type="password" required data-testid="admin-login-password-input" className={inputCls}
                            value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
                    </div>
                    {error && (
                        <p className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-xs text-rose-300" data-testid="admin-login-error">{error}</p>
                    )}
                    <button type="submit" disabled={busy} data-testid="admin-login-submit-button"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet-600 px-6 py-3 font-semibold text-white transition-all hover:bg-violet-500 disabled:opacity-60">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                        {busy ? "Signing in…" : "Sign In"}
                    </button>
                </form>
            </div>
        </main>
    );
}
